import Link from "next/link";

type ButtonVariant = "primary" | "secondary" | "accent" | "dark" | "darkSecondary";

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-white text-black hover:bg-white/90",
  secondary:
    "glass border-white/15 text-white hover:border-accent/30 hover:bg-white/[0.08] hover:shadow-[0_0_20px_rgba(13,168,159,0.1)]",
  accent:
    "bg-accent text-black hover:bg-accent-hover shadow-[0_0_24px_rgba(13,168,159,0.3)] hover:shadow-[0_0_40px_rgba(13,168,159,0.45)] hover:scale-[1.02]",
  dark: "bg-black text-white hover:bg-black/85 shadow-[0_0_20px_rgba(0,0,0,0.2)]",
  darkSecondary:
    "border border-black/15 bg-transparent text-black hover:bg-black/[0.04] hover:border-black/25",
};

type ButtonBaseProps = {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "md";
  className?: string;
  utmPlacement?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

type ButtonAsButton = ButtonBaseProps & {
  href?: undefined;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
};

type ButtonAsLink = ButtonBaseProps & {
  href: string;
  external?: boolean;
  type?: never;
  onClick?: never;
};

type ButtonProps = ButtonAsButton | ButtonAsLink;

const baseClasses =
  "font-ui inline-flex items-center justify-center rounded-full text-sm font-semibold transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:pointer-events-none";

const sizeClasses = {
  md: "px-6 py-3",
  sm: "px-4 py-2 text-[13px]",
};

export function Button({
  children,
  variant = "accent",
  size = "md",
  className = "",
  utmPlacement,
  "aria-label": ariaLabel,
  ...props
}: ButtonProps) {
  const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

  if ("href" in props && props.href) {
    const { href, external } = props;
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={classes}
          data-utm-placement={utmPlacement}
          aria-label={ariaLabel}
        >
          {children}
        </a>
      );
    }
    return (
      <Link href={href} className={classes} data-utm-placement={utmPlacement} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }

  const { type = "button", onClick, disabled } = props as ButtonAsButton;
  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      data-utm-placement={utmPlacement}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}
